import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { ProjectPlSummary, ProjectStatus } from '../../interfaces/project';
import { ProjectService } from '../../services/project.service';
import { useToast } from '../../context/ToastContext';
import { firstOfYearLocalIso, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { statusLabel } from './ProjectDialogs';

interface Applied { from: string; to: string; status: '' | ProjectStatus }

/** P1-A: revenue, expense and margin by project with an "Unassigned" bucket; ties to the income statement when unfiltered. */
const ProjectPlPage: React.FC = () => {
    const toast = useToast();
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [status, setStatus] = useState<'' | ProjectStatus>('');
    const [applied, setApplied] = useState<Applied | null>(null);
    const [report, setReport] = useState<ProjectPlSummary | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setReport(await ProjectService.projectPl({ from, to, status: status || undefined }));
            setApplied({ from, to, status });
        } catch (err) {
            toast.showApiError(err, 'Failed to load the project P&L');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, status]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Exports carry the APPLIED filters, never unapplied form edits.
    const download = async (format: 'pdf' | 'xlsx') => {
        if (!applied) return;
        try {
            await ProjectService.downloadProjectPl({ from: applied.from, to: applied.to, status: applied.status || undefined }, format);
        } catch (err) {
            toast.showApiError(err, 'Download failed');
        }
    };

    return (
        <Box data-help-page="projects.pl.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Project P&L</Typography>
            <Box data-help-section="projects.pl.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="projects.pl.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <TextField data-help="projects.pl.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <TextField data-help="projects.pl.status" select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value as '' | ProjectStatus)}
                    sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                    <MenuItem value="">All projects</MenuItem>
                    {(Object.keys(statusLabel) as ProjectStatus[]).map(s => <MenuItem key={s} value={s}>{statusLabel[s]}</MenuItem>)}
                </TextField>
                <Button data-help="projects.pl.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="projects.pl.excel" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('xlsx')} disabled={!applied}>Excel</Button>
                <Button data-help="projects.pl.pdf" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('pdf')} disabled={!applied}>PDF</Button>
            </Box>

            {report && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                        <Typography><strong>Revenue:</strong> {formatMoney(report.totalRevenue)}</Typography>
                        <Typography><strong>Expense:</strong> {formatMoney(report.totalExpense)}</Typography>
                        <Typography><strong>Margin:</strong> {formatMoney(report.totalMargin)}</Typography>
                    </Box>
                    <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 2 }}>
                        <Table data-help-section="projects.pl.table" size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="projects.pl.col.project">Project</TableCell><TableCell data-help="projects.pl.col.status">Status</TableCell>
                                    <TableCell data-help="projects.pl.col.revenue" align="right">Revenue</TableCell><TableCell data-help="projects.pl.col.expense" align="right">Expense</TableCell>
                                    <TableCell data-help="projects.pl.col.margin" align="right">Margin</TableCell><TableCell data-help="projects.pl.col.budget" align="right">Budget cost</TableCell>
                                    <TableCell data-help="projects.pl.col.used" align="right">% used</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.projects.length === 0 && (
                                    <TableRow><TableCell colSpan={7}>No revenue or expense in this period.</TableCell></TableRow>
                                )}
                                {report.projects.map(b => (
                                    <TableRow key={b.projectId ?? 'unassigned'} data-testid={`project-pl-${b.projectId ?? 'unassigned'}`}>
                                        <TableCell>{b.code ? `${b.code} ` : ''}{b.name}</TableCell>
                                        <TableCell>{b.status ? statusLabel[b.status] : ''}</TableCell>
                                        <TableCell align="right">{formatMoney(b.revenue)}</TableCell>
                                        <TableCell align="right">{formatMoney(b.expense)}</TableCell>
                                        <TableCell align="right">{formatMoney(b.margin)}</TableCell>
                                        <TableCell align="right">{b.budgetCost != null ? formatMoney(b.budgetCost) : ''}</TableCell>
                                        <TableCell align="right">{b.costUsedPct != null ? `${b.costUsedPct}%` : ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    {report.notes.map(n => (
                        <Typography key={n} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{n}</Typography>
                    ))}
                </>
            )}
        </Box>
    );
};

export default ProjectPlPage;
